'use client';

import { type Voice } from 'ml-text-service-client';
import Image from 'next/image';
import { type JSX, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useDebouncedCallback } from 'use-debounce';

import { Text, Tooltip, getAccessibleFocusStyles } from '@videoblocks/design-system';

import { useVoiceCard } from '../_hooks/useVoiceCard';
import { PlayButton } from './PlayButton';
import { formatDisplayText } from './utils';

/**
 * Detect if the device supports hover interactions
 * @returns boolean indicating if hover is supported
 */
function checkHasHover(): boolean {
    try {
        return matchMedia('(hover: hover)').matches;
    } catch {
        return true;
    }
}

const VoiceCard = ({
    voice,
    selectedVoice,
    setSelectedVoice,
    playingVoiceId,
    setPlayingVoiceId,
}: {
    voice: Voice;
    selectedVoice: Voice['voice_id'] | null;
    setSelectedVoice: (voiceId: Voice['voice_id']) => void;
    playingVoiceId?: Voice['voice_id'] | null;
    setPlayingVoiceId?: (voiceId: Voice['voice_id'] | null) => void;
}): JSX.Element => {
    const { descriptive, use_case, name, description, preview_url, voice_id } = voice;
    const formattedUseCase = formatDisplayText(use_case);

    const [hasImageError, setHasImageError] = useState(false);
    const [isDescriptionTruncated, setIsDescriptionTruncated] = useState(false);
    const [isLabelTruncated, setIsLabelTruncated] = useState(false);
    const descriptionRef = useRef<HTMLDivElement>(null);
    const labelRef = useRef<HTMLSpanElement>(null);
    const hasHover = checkHasHover();

    const imageSrc = `/assets/voiceoverAvatars/${use_case}.svg`;

    // Reset error state when image source changes (e.g., voice prop updates)
    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setHasImageError(false);
    }, [imageSrc]);

    const { isSelected, isPlaying, showPlayControls, setIsHovered, handleCardClick, handlePlayPause } = useVoiceCard({
        voiceId: voice_id,
        previewUrl: preview_url,
        selectedVoice,
        playingVoiceId: playingVoiceId ?? null,
        setSelectedVoice,
        setPlayingVoiceId,
    });

    const checkTruncation = useDebouncedCallback((): void => {
        const descriptionElement = descriptionRef.current;
        if (descriptionElement) {
            setIsDescriptionTruncated(descriptionElement.scrollHeight > descriptionElement.clientHeight);
        }
        const labelElement = labelRef.current;
        if (labelElement) {
            setIsLabelTruncated(labelElement.scrollWidth > labelElement.clientWidth);
        }
    }, 150);

    useLayoutEffect(() => {
        // Check immediately on mount
        checkTruncation();

        // Debounced resize handler to recalculate truncation when window size changes
        window.addEventListener('resize', checkTruncation);

        return () => {
            window.removeEventListener('resize', checkTruncation);
        };
    }, [description, checkTruncation]);

    const descriptionText = (
        <Text
            ref={descriptionRef}
            fontSize="sm"
            fontWeight="regular"
            className="overflow-x-hidden line-clamp-2 text-neutral-dark-800 text-ellipsis h-10"
        >
            {description}
        </Text>
    );

    const labelText = formattedUseCase + ', ' + formatDisplayText(descriptive);
    const labelSpan = (
        <span ref={labelRef} className="block truncate text-xs font-semibold text-yellow-600 capitalize">
            {labelText}
        </span>
    );

    return (
        <div
            role="button"
            tabIndex={0}
            aria-label={`Select voice ${name}`}
            aria-pressed={isSelected}
            data-testid="voice-card"
            onClick={handleCardClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onKeyDown={(event) => {
                if (event.target !== event.currentTarget || event.repeat) return;
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleCardClick();
                }
            }}
            className={twMerge(
                'bg-neutral-dark-200 border border-neutral-dark-300 cursor-pointer overflow-hidden flex gap-3 h-24 p-3 rounded-sm ',
                isSelected && 'ring-yellow-500 ring-2 border-transparent',
                getAccessibleFocusStyles(),
            )}
        >
            <div className="flex gap-3 h-full min-w-0">
                <div className="content-center">
                    <div className="shrink-0 w-13 h-13 rounded-full overflow-hidden focus-within:overflow-visible relative bg-gray-600">
                        {!hasImageError && (
                            <Image
                                src={imageSrc}
                                alt={`${name} voice avatar`}
                                width={52}
                                height={52}
                                className="w-full h-full object-cover"
                                onError={() => setHasImageError(true)}
                            />
                        )}

                        {hasImageError && (
                            <div
                                data-testid="image-error-gradient"
                                className="w-full h-full bg-linear-to-br from-orange-400 via-pink-500 to-purple-600"
                            />
                        )}
                        <PlayButton
                            previewUrl={preview_url}
                            isPlaying={isPlaying}
                            handlePlayPause={handlePlayPause}
                            size="lg"
                            className={twMerge(
                                'absolute inset-0 z-10 focus-visible:opacity-100',
                                showPlayControls ? 'opacity-100' : 'opacity-0 pointer-events-none',
                            )}
                        />
                    </div>
                </div>

                <div className="flex-1 h-full min-w-0">
                    <div className="flex items-center">
                        <Text className="pr-1 shrink-0 whitespace-nowrap">{name}</Text>
                        <div className="text-neutral-dark-800 pr-1 shrink-0">&middot;</div>
                        <div className="min-w-0">
                            {isLabelTruncated && hasHover ? (
                                <Tooltip title={labelText} variant="light" trigger={labelSpan} />
                            ) : (
                                labelSpan
                            )}
                        </div>
                    </div>
                    {isDescriptionTruncated && hasHover ? (
                        <Tooltip title={description} variant="light" trigger={<div>{descriptionText}</div>} />
                    ) : (
                        descriptionText
                    )}
                </div>
            </div>
        </div>
    );
};

export default VoiceCard;
